import { Facility } from "../../models";
import { FacilityEvent } from "../../models/FacilityEvent.gen";
import { FacilityEventType, getFacilityEventTypeDisplayName } from "../../models/Shared.FacilityEventType.gen";
import { Target } from "../../models/Target.gen";
import { Targets } from "../../api";
import { Icons } from "@app/dashboard/src/components/shared";

/**
 * Loads an event's target, or undefined when there isn't one to load.
 *
 * Every event-details popup needs this and each had grown its own copy that treated a missing target
 * as a hard failure — returning early or toasting "Failed to load target details" — so the popup never
 * opened at all. Dwell and presence hit that constantly (their targets are routinely purged before
 * anyone clicks the row), which meant the event's own facts — zone, lane, dwell time, occupant count —
 * were unreachable because a *supplementary* lookup failed.
 *
 * Never throws: callers open the dialog either way and render what they have.
 */
export const loadEventTarget = async (event: FacilityEvent): Promise<Target | undefined> => {
    if (!event.deviceId || !event.externalTargetId) return undefined;
    try {
        return await Targets.getTargetWithHistoryByExternalId(event.externalTargetId, event.deviceId);
    } catch {
        return undefined;
    }
};

export type EventDetailCard = { label: string; value: string; icon: typeof Icons.Route };

const NEAR_MISS_SEVERITY_LABELS = ["", "Minor", "Moderate", "Serious", "Severe", "Critical"];

/**
 * Whether the event's attached target actually describes the event.
 *
 * False for Detection Zone Presence: eventValue is the occupant COUNT, but the attached target is
 * whichever target's entry or exit triggered the recount — not one of the N, and on an exit event
 * it has already left the zone. Showing its type/lane/status therefore describes a vehicle that may
 * not even be in the zone. The event carries a single target id, so there is no list to show
 * instead; the honest option is to show none of it and keep what the event does assert (zone,
 * count, time, device).
 *
 * NearMiss has a related but different problem — two participants, only one of them persisted —
 * tracked separately.
 */
export const targetDescribesEvent = (event?: FacilityEvent) =>
    event?.eventType !== FacilityEventType.DetectionZonePresence;

export const getEventValueCards = (event: FacilityEvent, target?: Target): EventDetailCard[] => {
    switch (event.eventType) {
        case FacilityEventType.Speeding: {
            const eventSpeedMph = (event.eventValue ?? 0) * 2.23694;
            const cards: EventDetailCard[] = [{ label: "Speed at Detection", value: eventSpeedMph.toFixed(1) + " mph", icon: Icons.StopWatch }];
            const maxSpeedMs = target?.maximumVelocity;
            if (maxSpeedMs !== undefined && maxSpeedMs * 2.23694 > eventSpeedMph + 0.05) {
                cards.push({ label: "Max Speed", value: (maxSpeedMs * 2.23694).toFixed(1) + " mph", icon: Icons.StopWatch });
            }
            return cards;
        }
        case FacilityEventType.WrongWay:
            return [{ label: "Heading", value: event.eventValue + "°", icon: Icons.ArrowRight }];
        case FacilityEventType.ScreenLineChange:
        case FacilityEventType.DetectionZoneChange:
            return [{ label: "Direction", value: event.eventValue >= 1 ? "Entry" : "Exit", icon: event.eventValue >= 1 ? Icons.Entry : Icons.Exit }];
        case FacilityEventType.DetectionZonePresence:
            return [{ label: "Targets in Zone", value: (event.eventValue ? event.eventValue : "No") + " Target" + (event.eventValue !== 1 ? "s" : ""), icon: Icons.Users }];
        case FacilityEventType.DetectionZoneDwell: {
            // EventValue is the configured trigger threshold (historical meaning, preserved).
            // EventValue2 is the actual observed dwell — only populated on newer rows.
            const actual = event.eventValue2;
            const threshold = event.eventValue ?? 0;
            const seconds = actual ?? threshold;
            const prefix = actual != null ? "" : ">";
            return [{ label: "Dwell Time", value: `${prefix}${seconds} second${seconds !== 1 ? "s" : ""}`, icon: Icons.Clock }];
        }
        case FacilityEventType.NearMiss: {
            const ev2 = event.eventValue2 ?? 0;
            const ttcMs = Math.floor(ev2 / 1000) * 10;
            const angle = ev2 % 1000;
            return [
                { label: "Time To Contact", value: `${ttcMs} ms`, icon: Icons.Clock },
                { label: "Conflict Angle", value: `${angle}°`, icon: Icons.ArrowRight },
            ];
        }
        default:
            return [];
    }
};

export const getEventDialogTitle = (event: FacilityEvent): string => {
    if (event.eventType === FacilityEventType.NearMiss) {
        const severity = NEAR_MISS_SEVERITY_LABELS[event.eventValue ?? 0];
        return `${severity ? severity + " " : ""}Near Miss Event Details`;
    }
    return `${getFacilityEventTypeDisplayName(event.eventType) ?? "Event"} Event Details`;
};

// The single resolver for "which piece of geometry does this event belong to". This logic was copied
// into five places and they drifted: the two detail popups and the firmware events table never checked
// laneLineId, so every lane-line event rendered a blank Trigger while the same event showed a name in
// the facility and organization tables. Call this instead of re-deriving it.
export const getEventTriggerName = (event: FacilityEvent, facility: Facility | undefined): string | undefined => {
    if (!facility) return undefined;
    if (event.screenLineId) return facility.screenLines?.find((sl) => sl.id === event.screenLineId)?.name;
    if (event.detectionZoneId) return facility.detectionZones?.find((dz) => dz.id === event.detectionZoneId)?.name;
    if (event.laneLineId) return facility.laneLines?.find((ll) => ll.id === event.laneLineId)?.name;
    return undefined;
};
